import * as React from "react"
import { Clock } from "lucide-react"
import { cn } from "@verlo/shared/lib/utils"
import { Popover, PopoverAnchor, PopoverContent } from "./popover"
import { nearestStep, parseTime, timeSteps } from "@verlo/shared/lib/times"

/**
 * The one time field in the back office (Back office sweep, Oct 2026: D8). Type it ("1830"
 * becomes 18:30, "6pm" 18:00), or pick from a short list in 15-minute steps that opens on the
 * time you have. Styled like every other field; the browser's own time input looked different
 * on every machine and couldn't be typed into the way people say times.
 *
 *   <TimeField value="18:00" onChange={(t) => …} aria-label="From" />
 *
 * `value` and `onChange` are "HH:MM", or "" when `allowEmpty` lets the field be cleared.
 */
const STEPS = timeSteps(15)

function TimeField({ value, onChange, allowEmpty = false, placeholder = "--:--", className, testId, disabled, ...rest }) {
  const [text, setText] = React.useState(value || "")
  const [open, setOpen] = React.useState(false)
  const listRef = React.useRef(null)
  React.useEffect(() => { setText(value || "") }, [value])

  const commit = (raw) => {
    if (allowEmpty && !String(raw).trim()) { setText(""); if (value) onChange(""); return }
    const t = parseTime(raw)
    if (t) { setText(t); if (t !== value) onChange(t) } else setText(value || "")
  }
  const pick = (t) => { setText(t); setOpen(false); if (t !== value) onChange(t) }

  // Open on the time you have, not at midnight.
  React.useEffect(() => {
    if (!open) return
    const near = nearestStep(text || value) || "12:00"
    requestAnimationFrame(() => listRef.current?.querySelector(`[data-time="${near}"]`)?.scrollIntoView({ block: "center" }))
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className={cn(
          "flex h-9 w-28 items-center gap-1.5 rounded-md border border-input bg-white px-2.5 focus-within:border-verlo focus-within:ring-1 focus-within:ring-inset focus-within:ring-verlo",
          disabled && "opacity-50", className,
        )}>
          <Clock size={14} className="shrink-0 text-ink-faint" aria-hidden="true" />
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={text}
            placeholder={placeholder}
            disabled={disabled}
            onChange={(e) => setText(e.target.value)}
            onFocus={() => setOpen(true)}
            onBlur={(e) => { commit(e.target.value) }}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); commit(e.currentTarget.value); setOpen(false) }
              if (e.key === "Escape") { setText(value || ""); setOpen(false) }
            }}
            className="w-full min-w-0 bg-transparent font-mono text-[14px] tabular-nums text-ink outline-none placeholder:text-ink-faint"
            data-testid={testId}
            {...rest}
          />
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        className="w-28 p-1"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="px-2 pb-1 pt-0.5 text-[11px] text-ink-faint">Type it, or pick</div>
        <div ref={listRef} className="max-h-48 overflow-y-auto" role="listbox">
          {STEPS.map((t) => (
            <button
              key={t}
              type="button"
              role="option"
              aria-selected={t === value}
              data-time={t}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(t)}
              className={cn(
                "block w-full rounded px-2 py-1 text-left font-mono text-[13.5px] tabular-nums hover:bg-hover-tint",
                t === value ? "bg-verlo-tint font-semibold text-verlo" : "text-ink",
              )}
            >
              {t}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export { TimeField }
